import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import {
  applyCreateDashboardSharedSessionToCache,
  postDashboardSharedSession,
  type CreateDashboardSharedSessionResponse,
} from "./create-dashboard-shared-session";
import type { SharedSessionTarget } from "./shared-session-cache-utils";

import { useAuth } from "@/context/auth-context";
import { displayErrorToast } from "@/utils/toast";

export interface CreateSharedSessionRequest {
  workspace_id: number;
  name: string;
  prompt_id: number;
  label_name?: string;
  version_number?: number;
  is_public: boolean;
  batch_enabled: boolean;
  chat_enabled: boolean;
  chat_settings?: Record<string, unknown>;
  anonymous_daily_limit?: number;
  remix_enabled: boolean;
}

export interface CreateSharedSkillCollectionRequest {
  workspace_id: number;
  name: string;
  skill_collection_id: string;
  label_name?: string;
  version_number?: number;
  is_public: boolean;
  remix_enabled: boolean;
}

function useDashboardSharedSessionCreateMutation<
  TRequest extends { workspace_id: number },
  TResponse extends CreateDashboardSharedSessionResponse,
>(options: {
  buildBody: (request: TRequest) => Record<string, unknown>;
  getCacheTarget: (request: TRequest) => SharedSessionTarget;
}): UseMutationResult<TResponse, Error, TRequest> {
  const authContext = useAuth();
  const queryClient = useQueryClient();

  return useMutation<TResponse, Error, TRequest>({
    mutationFn: async request => {
      const { workspace_id } = request;
      return postDashboardSharedSession(
        authContext?.userToken,
        workspace_id,
        options.buildBody(request),
      ) as Promise<TResponse>;
    },

    onSuccess: (data, request) => {
      applyCreateDashboardSharedSessionToCache(queryClient, data, options.getCacheTarget(request));
    },

    onError: error => {
      displayErrorToast(error.message || "Failed to create shared session");
    },
  });
}

export const useSharePrompt = () =>
  useDashboardSharedSessionCreateMutation<
    CreateSharedSessionRequest,
    CreateDashboardSharedSessionResponse
  >({
    buildBody: request => {
      const { workspace_id: _workspaceId, ...body } = request;
      return { ...body, resource_type: "prompt_template" };
    },
    getCacheTarget: request => ({
      workspaceId: request.workspace_id,
      promptId: request.prompt_id,
    }),
  });

export const useShareSkillCollection = () =>
  useDashboardSharedSessionCreateMutation<
    CreateSharedSkillCollectionRequest,
    CreateDashboardSharedSessionResponse
  >({
    buildBody: request => {
      const { workspace_id: _workspaceId, ...body } = request;
      return {
        ...body,
        resource_type: "skill_collection",
        batch_enabled: false,
        chat_enabled: false,
      };
    },
    getCacheTarget: request => ({
      workspaceId: request.workspace_id,
      skillCollectionId: request.skill_collection_id,
    }),
  });
